import { Component, OnDestroy, OnInit } from "@angular/core";
import { CommonModule, formatDate } from "@angular/common";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { catchError, of, Subject, takeUntil, tap } from "rxjs";

import { API_ENDPOINTS } from "../../../shared/services/api/api-endpoints";
import { ApiService } from "../../../shared/services/api/api.service";
import { ApiResponse } from "../../../shared/models/shared.model";
import { LoaderComponent } from "../../../shared/components/loader/loader.component";
import { environment } from "../../../../environments/environment";

@Component({
  selector: "customer-promo-codes",
  standalone: true,
  imports: [CommonModule, LoaderComponent, ReactiveFormsModule, FormsModule],
  templateUrl: "./promo-codes.component.html",
  styleUrl: "./promo-codes.component.scss",
})
export class CustomerPromoCodesComponent implements OnInit, OnDestroy {
  assetUrl = environment.assetBaseUrl;

  promoVouchers: any[] = [];
  redeempromocode = "";
  isLoading = false;
  promoCode = "";
  promoIsNull = false;

  private destroy$ = new Subject<void>();

  constructor(private apiService: ApiService) {}

  ngOnInit(): void {
    this.getPromoVouchers().pipe(takeUntil(this.destroy$)).subscribe();
  }

  get activeVouchers(): number {
    return this.promoVouchers.filter(
      (voucher) => this.voucherStatusLabel(voucher) === "Active",
    ).length;
  }

  get expiredVouchers(): number {
    return this.promoVouchers.filter((voucher) => this.isExpired(voucher))
      .length;
  }

  get percentageVouchers(): number {
    return this.promoVouchers.filter(
      (voucher) => voucher.discount_type === "PERCENT",
    ).length;
  }

  get fixedVouchers(): number {
    return this.promoVouchers.filter(
      (voucher) => voucher.discount_type === "FIXED",
    ).length;
  }

  get endingSoonVouchers(): number {
    const now = new Date();

    return this.promoVouchers.filter((voucher) => {
      if (!voucher.end_date) return false;

      const endDate = new Date(voucher.end_date);

      if (Number.isNaN(endDate.getTime())) return false;

      const diffMs = endDate.getTime() - now.getTime();
      const diffDays = diffMs / (1000 * 60 * 60 * 24);

      return diffDays >= 0 && diffDays <= 14;
    }).length;
  }

  get highestVoucherValue(): string {
    if (!this.promoVouchers.length) return "0";

    const highest = this.promoVouchers.reduce((best, voucher) => {
      const value = Number(voucher.discount_value || 0);
      const bestValue = Number(best.discount_value || 0);

      return value > bestValue ? voucher : best;
    }, this.promoVouchers[0]);

    return this.formatDiscountValue(highest);
  }

  getPromoVouchers() {
    this.isLoading = true;

    return this.apiService
      .request<ApiResponse<[]>>(API_ENDPOINTS.customer.promoCode.list, "GET")
      .pipe(
        tap((response: any) => {
          this.isLoading = false;
          this.promoVouchers = response.results || [];
        }),
        catchError(() => {
          this.isLoading = false;
          this.promoVouchers = [];
          return of([]);
        }),
      );
  }

  redeemPromoVouchers(code: string): void {
    const cleanCode = (code || "").trim();

    if (!cleanCode) {
      this.promoIsNull = true;
      return;
    }

    this.promoIsNull = false;
    this.isLoading = true;

    this.apiService
      .request<[]>(
        API_ENDPOINTS.customer.promoCode.redeemVoucher(cleanCode),
        "POST",
        { code: cleanCode },
      )
      .pipe(
        tap((response: any) => {
          this.promoVouchers = Array.isArray(response)
            ? response
            : response?.results || this.promoVouchers;

          this.promoCode = "";
          this.isLoading = false;
        }),
        catchError((error) => {
          console.error("Promo redeem error:", error, cleanCode);
          this.isLoading = false;
          return of([]);
        }),
      )
      .subscribe();
  }

  trackByVoucherID(index: number, item: any): string | number {
    return item.id || item.promotion_id || item.code || index;
  }

  formatDiscountValue(voucher: any): string {
    const value = Number(voucher?.discount_value || 0);

    if (voucher?.discount_type === "PERCENT") {
      return `${Number.isFinite(value) ? value : 0}%`;
    }

    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    }).format(Number.isFinite(value) ? value : 0);
  }

  formatDate(dateString: string): string {
    if (!dateString) return "";

    const date = new Date(dateString);

    if (Number.isNaN(date.getTime())) return "";

    return formatDate(date, "MMM d, y", "en-US");
  }

  isExpired(voucher: any): boolean {
    if (!voucher?.end_date) return false;

    const endDate = new Date(voucher.end_date);

    if (Number.isNaN(endDate.getTime())) return false;

    return endDate < new Date();
  }

  voucherStatusLabel(voucher: any): string {
    if (this.isExpired(voucher)) return "Expired";

    if (voucher?.is_active === false) return "Inactive";

    return "Active";
  }

  voucherStatusClass(voucher: any): string {
    const status = this.voucherStatusLabel(voucher);

    if (status === "Expired") return "status-expired";
    if (status === "Inactive") return "status-inactive";

    return "status-active";
  }

  copyPromoCode(code: string): void {
    if (!code) return;

    navigator.clipboard?.writeText(code);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
